import React from 'react';
import isEmpty from 'lodash/isEmpty';
import isUndefined from 'lodash/isUndefined';
import parse from 'html-react-parser';

import { FormattedMessage } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faBanSmoking,
  faBellConcierge,
  faCalendarDay,
  faCalendarDays,
  faCircleInfo,
  faIdCard,
  faMoon,
  faPaw,
  faSquareParking,
} from '@fortawesome/free-solid-svg-icons';

import { IDetails } from '@share/common-types';
import { to12Hour } from '@share/utils';

import './style.scss';

interface IProps {
  condoDetails: IDetails;
  refAnchor: React.RefObject<HTMLDivElement>;
}

const ZERO = 0;

export class CondoPolicies extends React.Component<IProps> {
  isWeeksResortCheckInDays = (): boolean => {
    const { daysOfCheckIn } = this.props.condoDetails;

    return (
      !isUndefined(daysOfCheckIn) &&
      !isUndefined(daysOfCheckIn.weeksResortCheckInDays) &&
      !isEmpty(daysOfCheckIn.weeksResortCheckInDays)
    );
  };

  isDefaultTextWeeksResortCheckInDays = (): boolean => {
    const { daysOfCheckIn } = this.props.condoDetails;

    return (
      !isUndefined(daysOfCheckIn) &&
      !isUndefined(daysOfCheckIn.pointsResortCheckInDays) &&
      !isEmpty(daysOfCheckIn.pointsResortCheckInDays) &&
      (isUndefined(daysOfCheckIn.weeksResortCheckInDays) ||
        isEmpty(daysOfCheckIn.weeksResortCheckInDays))
    );
  };

  isPointsResortCheckInDays = (): boolean => {
    const { daysOfCheckIn } = this.props.condoDetails;

    return (
      !isUndefined(daysOfCheckIn) &&
      !isUndefined(daysOfCheckIn.pointsResortCheckInDays) &&
      !isEmpty(daysOfCheckIn.pointsResortCheckInDays)
    );
  };

  isCheckInTime = (): boolean => {
    const { checkInCheckOutInfo } = this.props.condoDetails;

    return !isUndefined(checkInCheckOutInfo) && !isUndefined(checkInCheckOutInfo.checkInTime);
  };

  isCheckOutTime = (): boolean => {
    const { checkInCheckOutInfo } = this.props.condoDetails;

    return !isUndefined(checkInCheckOutInfo) && !isUndefined(checkInCheckOutInfo.checkOutTime);
  };

  isPossibleApplicableFees = (): boolean => {
    const mandatoryFees = (this.props.condoDetails.mandatoryFees || []).filter(fee => Object.keys(fee).length > 0);
    const fees = (this.props.condoDetails.fees || []).filter(fee => Object.keys(fee).length > 0);
    const housekeepingFees = (this.props.condoDetails.housekeepingFees || []).filter(fee => Object.keys(fee).length > 0);

    return (
      !!mandatoryFees.length ||
      !!fees.length ||
      !!housekeepingFees.length
    );
  };

  isKnowBeforeYouGo = (): boolean => {
    const { urgentInfo, constructionDetails } = this.props.condoDetails;

    return !isEmpty(urgentInfo) || !isEmpty(constructionDetails);
  };

  isShowPolicies = (): boolean => {
    const {
      ageRestrictions,
      minimumNights,
      frontDeskInfo,
      pets,
      parking,
      smoking,
      orientation,
      policiesHighlights,
    } = this.props.condoDetails;

    return (
      !isEmpty(policiesHighlights) ||
      this.isCheckInTime() ||
      this.isCheckOutTime() ||
      (!isUndefined(ageRestrictions) &&
        !isEmpty(ageRestrictions) &&
        this.isWeeksResortCheckInDays()) ||
      this.isDefaultTextWeeksResortCheckInDays() ||
      this.isPointsResortCheckInDays() ||
      minimumNights > ZERO ||
      (!isUndefined(frontDeskInfo) && !isEmpty(frontDeskInfo)) ||
      !isEmpty(pets) ||
      !isEmpty(parking) ||
      !isEmpty(smoking) ||
      !isEmpty(orientation) ||
      this.isKnowBeforeYouGo() ||
      this.isPossibleApplicableFees()
    );
  };

  renderPolicyCard = (icon: IconDefinition, labelId: string, children: React.ReactNode): React.ReactNode => (
    <div className="condo-policies__item">
      <span className="condo-policies__item-icon">
        <FontAwesomeIcon icon={icon} />
      </span>
      <div className="condo-policies__item-content">
        <p className="condo-policies__item-label">
          <FormattedMessage id={labelId} />
        </p>
        <div className="condo-policies__item-text">{children}</div>
      </div>
    </div>
  );

  render(): React.ReactNode {
    const { condoDetails, refAnchor } = this.props;
    const {
      checkInCheckOutInfo,
      checkOutInformation,
      checkInInformation,
      ageRestrictions,
      daysOfCheckIn,
      minimumNights,
      frontDeskInfo,
      pets,
      parking,
      smoking,
      orientation,
      urgentInfo,
      mandatoryFees,
      fees,
      housekeepingFees,
      constructionDetails,
    } = condoDetails;

    const hasTimes = this.isCheckInTime() || this.isCheckOutTime();
    const hasPolicyItems =
      (!isUndefined(ageRestrictions) && !isEmpty(ageRestrictions)) ||
      this.isWeeksResortCheckInDays() ||
      this.isDefaultTextWeeksResortCheckInDays() ||
      this.isPointsResortCheckInDays() ||
      minimumNights > ZERO ||
      (!isUndefined(frontDeskInfo) && !isEmpty(frontDeskInfo)) ||
      !isEmpty(pets) ||
      !isEmpty(parking) ||
      !isEmpty(smoking) ||
      !isEmpty(orientation);

    return this.isShowPolicies() ? (
      <div className="condo-policies" ref={refAnchor}>
        <h2 className="condo-policies__title secondary-font">
          <FormattedMessage id="tab.policies" />
        </h2>
        <div className="condo-policies__highlights-wrapper">
          {condoDetails.policiesHighlights?.map((item, index) => {
            return (
              <p key={index} className="condo-policies__highlights">
                {parse(item)}
              </p>
            );
          })}
        </div>
        {(hasTimes || hasPolicyItems) && (
          <div className="condo-policies__card">
            {(this.isCheckInTime() || this.isCheckOutTime()) && (
          <div className="condo-policies__times">
            {this.isCheckInTime() ? (
              <div className="condo-policies__time">
                <p className="condo-policies__time-label">
                  <FormattedMessage id="check.in" />
                </p>
                <p className="condo-policies__time-value">{to12Hour(checkInCheckOutInfo.checkInTime)}</p>
                {!isEmpty(checkInInformation) && (
                  <div className="condo-policies__time-extra">
                    {checkInInformation.map((checkIn, index) => (
                      <p key={index} className="condo-policies__time-extra-text">
                        {to12Hour(checkIn)}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            ) : null}
            {this.isCheckOutTime() ? (
              <div className="condo-policies__time">
                <p className="condo-policies__time-label">
                  <FormattedMessage id="check.out" />
                </p>
                <p className="condo-policies__time-value">{to12Hour(checkInCheckOutInfo.checkOutTime)}</p>
                {!isEmpty(checkOutInformation) && (
                  <div className="condo-policies__time-extra">
                    {checkOutInformation.map((checkOut, index) => (
                      <p key={index} className="condo-policies__time-extra-text">
                        {to12Hour(checkOut)}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            ) : null}
          </div>
        )}

        <div className="condo-policies__items">
          {!isUndefined(ageRestrictions) && !isEmpty(ageRestrictions)
            ? this.renderPolicyCard(
                faIdCard,
                'age.restriction',
                ageRestrictions.map((item, index) => (
                  <p key={index} className="condo-policies__info-text">
                    {item}
                  </p>
                )),
              )
            : null}
          {this.isWeeksResortCheckInDays()
            ? this.renderPolicyCard(
                faCalendarDays,
                'weekly.stays',
                daysOfCheckIn.weeksResortCheckInDays.map((item, index) => (
                  <p key={index} className="condo-policies__info-text bold">
                    {item}
                  </p>
                )),
              )
            : null}
          {this.isDefaultTextWeeksResortCheckInDays()
            ? this.renderPolicyCard(
                faCalendarDays,
                'weekly.stays',
                <FormattedMessage id="not.returned.days.of.check.in.for.weekly" />,
              )
            : null}
          {this.isPointsResortCheckInDays()
            ? this.renderPolicyCard(
                faCalendarDay,
                'short.stays',
                daysOfCheckIn.pointsResortCheckInDays.map((item, index) => (
                  <p key={index} className="condo-policies__info-text bold">
                    {item}
                  </p>
                )),
              )
            : null}
          {minimumNights > ZERO
            ? this.renderPolicyCard(
                faMoon,
                'minimum.night.stay',
                <p className="condo-policies__info-text bold">{minimumNights}</p>,
              )
            : null}
          {!isUndefined(frontDeskInfo) && !isEmpty(frontDeskInfo)
            ? this.renderPolicyCard(
                faBellConcierge,
                'front.desk.info',
                <>
                  <p className="condo-policies__info-text bold">
                    {to12Hour(frontDeskInfo?.hoursOfOperationStartTime)} -{' '}
                    {to12Hour(frontDeskInfo?.hoursOfOperationEndTime)}
                  </p>
                  {frontDeskInfo.resortFrontDeskInfoIs24Hours && (
                    <p className="condo-policies__info-text bold">
                      <FormattedMessage id="24.hours" />
                    </p>
                  )}
                  {frontDeskInfo.resortFrontdeskInfoIsOnsite && (
                    <p className="condo-policies__info-text bold">
                      <FormattedMessage id="onsite" />
                    </p>
                  )}
                </>,
              )
            : null}
          {!isEmpty(pets)
            ? this.renderPolicyCard(
                faPaw,
                'pets',
                pets.map((item, index) => (
                  <p key={index} className="condo-policies__info-text">
                    {item}
                  </p>
                )),
              )
            : null}
          {!isEmpty(parking)
            ? this.renderPolicyCard(
                faSquareParking,
                'parking',
                parking.map((item, index) => (
                  <p key={index} className="condo-policies__info-text">
                    {item}
                  </p>
                )),
              )
            : null}
          {!isEmpty(smoking)
            ? this.renderPolicyCard(
                faBanSmoking,
                'smoking',
                smoking.map((item, index) => (
                  <p key={index} className="condo-policies__info-text">
                    {item}
                  </p>
                )),
              )
            : null}
          {!isEmpty(orientation)
            ? this.renderPolicyCard(
                faCircleInfo,
                'other',
                orientation.map((item, index) => (
                  <p key={index} className="condo-policies__info-text">
                    {item}
                  </p>
                )),
              )
            : null}
            </div>
          </div>
        )}
        {this.isKnowBeforeYouGo() || this.isPossibleApplicableFees() ? (
          <div className="condo-policies__know-wrapper">
            {!isEmpty(urgentInfo) && (
              <div className="condo-policies__know-block">
                <h4 className="condo-policies__know-title">
                  <FontAwesomeIcon icon={faCircleInfo} className="condo-policies__know-title-icon" />
                  <FormattedMessage id="know.before.you.go" />
                </h4>
                {!isEmpty(urgentInfo.generalUrgentInfoMessages) && (
                  <div className="condo-policies__know-text-wrapper">
                    {urgentInfo.generalUrgentInfoMessages.map((item, index) => (
                      <p key={index} className="condo-policies__know--text">
                        {parse(item)}
                      </p>
                    ))}
                  </div>
                )}
                {!isEmpty(urgentInfo.generalUrgentInfoForNonMembersMessages) && (
                  <div className="condo-policies__know-text-wrapper">
                    {urgentInfo.generalUrgentInfoForNonMembersMessages.map((item, index) => (
                      <p key={index} className="condo-policies__know--text">
                        {parse(item)}
                      </p>
                    ))}
                  </div>
                )}
                {urgentInfo.occupancyRestrictions && (
                  <div className="condo-policies__know-text-wrapper">
                    <p className="condo-policies__know--text">{parse(urgentInfo.occupancyRestrictions)}</p>
                  </div>
                )}
                {urgentInfo.oneInXRuleApplies && (
                  <div className="condo-policies__know-text-wrapper">
                    <p className="condo-policies__know--text">{parse(urgentInfo.oneInXRuleApplies)}</p>
                  </div>
                )}
                {urgentInfo.separateSwimmingPoolForGenders && (
                  <div className="condo-policies__know-text-wrapper">
                    <p className="condo-policies__know--text">
                      <FormattedMessage id="separate.swimming.pool.for.genders" />
                    </p>
                  </div>
                )}
                {urgentInfo.alcoholRestricted && (
                  <div className="condo-policies__know-text-wrapper">
                    <p className="condo-policies__know--text">
                      <FormattedMessage id="alcohol.restricted" />
                    </p>
                  </div>
                )}
                {urgentInfo.alcoholServedOnsite && (
                  <div className="condo-policies__know-text-wrapper">
                    <p className="condo-policies__know--text">
                      <FormattedMessage id="alcohol.served.onsite" />
                    </p>
                  </div>
                )}
                {urgentInfo.resortStayRestrictionsInfo && (
                  <div className="condo-policies__know-text-wrapper">
                    <p className="condo-policies__know--text">
                      {parse(urgentInfo.resortStayRestrictionsInfo)}
                    </p>
                  </div>
                )}
              </div>
            )}
            {this.isPossibleApplicableFees() && (
              <div className="condo-policies__know-block">
                <h4 className="condo-policies__know-title">
                  <FontAwesomeIcon icon={faCircleInfo} className="condo-policies__know-title-icon" />
                  <FormattedMessage id="possible.applicable.fees" />
                </h4>
                {!isEmpty(mandatoryFees) && (
                  <div className="condo-policies__know-text-wrapper">
                    {mandatoryFees.map((item, index) => (
                      <div key={index} className="condo-policies__know-text-block">
                        {item.infoMessage && (
                          <p className="condo-policies__know--text">{parse(item.infoMessage)}</p>
                        )}
                        {item.additionalInfoMessage && (
                          <p className="condo-policies__know--text">{parse(item.additionalInfoMessage)}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {!isEmpty(fees) && (
                  <div className="condo-policies__know-text-wrapper">
                    {fees.map((item, index) => (
                      <div key={index} className="condo-policies__know-text-block">
                        {item.infoMessage && (
                          <p className="condo-policies__know--text">{parse(item.infoMessage)}</p>
                        )}
                        {item.additionalInfoMessage && (
                          <p className="condo-policies__know--text">{parse(item.additionalInfoMessage)}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {!isEmpty(housekeepingFees) && (
                  <div className="condo-policies__know-text-wrapper">
                    {housekeepingFees.map((item, index) => (
                      <div key={index} className="condo-policies__know-text-block">
                        {item.feeSchedule && (
                          <p className="condo-policies__know--text">{parse(item.feeSchedule)}</p>
                        )}
                        {item.infoMessage && (
                          <p className="condo-policies__know--text">{parse(item.infoMessage)}</p>
                        )}
                        {item.additionalInfoMessage && (
                          <p className="condo-policies__know--text">{parse(item.additionalInfoMessage)}</p>
                        )}
                        {item.mandatoryFee && (
                          <p className="condo-policies__know--text">
                            <FormattedMessage id="mandatory.fee" />
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {!isEmpty(constructionDetails) && (
              <div className="condo-policies__know-block">
                <h4 className="condo-policies__know-title">
                  <FontAwesomeIcon icon={faCircleInfo} className="condo-policies__know-title-icon" />
                  <FormattedMessage id="resort.construction.restrictions" />
                </h4>
                <div className="condo-policies__know-text-wrapper">
                  {constructionDetails.map((item, index) => (
                    <div key={index}>
                      {item.startDate && item.endDate && (
                        <p className="condo-policies__know--text">
                          {item.startDate} - {item.endDate}
                        </p>
                      )}
                      {item.infoMessage && (
                        <p className="condo-policies__know--text">{parse(item.infoMessage)}</p>
                      )}
                      {item.additionalInfoMessage && (
                        <p className="condo-policies__know--text">{parse(item.additionalInfoMessage)}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : null}
      </div>
    ) : null;
  }
}

